import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  addReply,
  getCommentReplies,
  updateReply as updateReplyInFirestore,
  deleteReply,
} from "../../../firebase/repliesApi";
import {
  adjustPendingCommentDeltas,
  incrementPostCommentCount,
} from "../postSlice.js";
import { fetchUsersByIds } from "../usersCacheSlice.js";
import { readCache, writeCache } from "./cache.js";

// --- Hydration thunk ---
const mergeReplyLikeState = (replies, likedReplyIds) =>
  (replies || []).map((reply) => ({
    ...reply,
    isLiked: !!likedReplyIds?.[reply.id],
  }));

export const hydrateRepliesFromCache = createAsyncThunk(
  "replies/hydrateRepliesFromCache",
  async (commentId, { rejectWithValue, getState }) => {
    try {
      const cached = readCache(commentId);
      const likedReplyIds = getState()?.commentLikes?.likedReplyIds || {};
      const replies = mergeReplyLikeState(cached, likedReplyIds);
      return { commentId, replies };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to hydrate replies");
    }
  },
);

// --- Async thunks ---
export const fetchCommentReplies = createAsyncThunk(
  "replies/fetchCommentReplies",
  async (commentId, { rejectWithValue, dispatch, getState }) => {
    try {
      const rawReplies = await getCommentReplies(commentId);
      const likedReplyIds = getState()?.commentLikes?.likedReplyIds || {};
      const replies = mergeReplyLikeState(rawReplies, likedReplyIds);

      // Prefetch user profiles
      try {
        const ids = Array.from(
          new Set((replies || []).map((r) => r.userId).filter(Boolean)),
        );
        if (ids.length > 0) dispatch(fetchUsersByIds({ userIds: ids }));
      } catch {}

      // Cache to localStorage
      writeCache(commentId, replies);

      return { commentId, replies };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const createReply = createAsyncThunk(
  "replies/createReply",
  async (
    { commentId, postId, replyData, tempId },
    { rejectWithValue, dispatch, getState },
  ) => {
    // Guard: reject replies on optimistic (not-yet-reconciled) parent comments.
    // The Firestore rule for /replies/{replyId} requires get(/comments/<id>).data != null,
    // which fails for temp-* ids that exist only in Redux state. Block here with a clear
    // message instead of letting Firestore reject with a cryptic permissions error.
    if (typeof commentId !== "string" || commentId.startsWith("temp-")) {
      return rejectWithValue(
        "Please wait a moment — the comment is still being saved.",
      );
    }

    let pid =
      postId ||
      Object.values(getState().comments?.items || {})
        .flat()
        .find((c) => c.id === commentId)?.postId ||
      null;

    if (pid) {
      dispatch(
        adjustPendingCommentDeltas({
          postId: pid,
          commentCount: 1,
          topLevelCommentCount: 0,
        }),
      );
    }

    try {
      const serverReply = await addReply(commentId, replyData);
      if (!serverReply?.id) throw new Error("addReply returned no ID");

      const resolvedPostId = serverReply.postId || pid;

      if (resolvedPostId) {
        dispatch(
          adjustPendingCommentDeltas({
            postId: resolvedPostId,
            commentCount: -1,
            topLevelCommentCount: 0,
          }),
        );
        dispatch(
          incrementPostCommentCount({
            postId: resolvedPostId,
            delta: 1,
          }),
        );
      }

      // Prefetch user profile
      try {
        if (serverReply.userId) {
          dispatch(fetchUsersByIds({ userIds: [serverReply.userId] }));
        }
      } catch {}

      return { commentId, postId: resolvedPostId, tempId, reply: serverReply };
    } catch (error) {
      if (pid) {
        dispatch(
          adjustPendingCommentDeltas({
            postId: pid,
            commentCount: -1,
            topLevelCommentCount: 0,
          }),
        );
        dispatch(
          incrementPostCommentCount({
            postId: pid,
            delta: -1,
          }),
        );
      }
      return rejectWithValue(error.message);
    }
  },
);

export const updateReply = createAsyncThunk(
  "replies/updateReply",
  async ({ commentId, replyId, text }, { rejectWithValue, getState }) => {
    try {
      const username = getState()?.auth?.userData?.username || "User";
      await updateReplyInFirestore(replyId, { text, username });
      return { commentId, replyId, text };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const removeReply = createAsyncThunk(
  "replies/removeReply",
  async (
    { replyId, commentId, postId },
    { rejectWithValue, dispatch, getState },
  ) => {
    let resolvedPostId = postId;
    try {
      const state = getState();

      // Try to find postId from rollback state
      if (!resolvedPostId) {
        const rollbackEntry = state.replies?.rollback?.[commentId]?.[replyId];
        if (rollbackEntry) {
          resolvedPostId = rollbackEntry._postId || rollbackEntry.postId;
        }
      }

      // Fallback: find from comments state
      if (!resolvedPostId) {
        const comment = Object.values(state.comments?.items || {})
          .flat()
          .find((c) => c.id === commentId);
        resolvedPostId = comment?.postId;
      }

      if (resolvedPostId) {
        dispatch(
          adjustPendingCommentDeltas({
            postId: resolvedPostId,
            commentCount: -1,
            topLevelCommentCount: 0,
          }),
        );
        dispatch(incrementPostCommentCount({ postId: resolvedPostId, delta: -1 }));
      }

      await deleteReply(replyId);

      if (resolvedPostId) {
        dispatch(
          adjustPendingCommentDeltas({
            postId: resolvedPostId,
            commentCount: 1,
            topLevelCommentCount: 0,
          }),
        );
      }

      return { replyId, commentId, postId: resolvedPostId };
    } catch (error) {
      if (resolvedPostId) {
        dispatch(
          adjustPendingCommentDeltas({
            postId: resolvedPostId,
            commentCount: 1,
            topLevelCommentCount: 0,
          }),
        );
        dispatch(incrementPostCommentCount({ postId: resolvedPostId, delta: 1 }));
      }
      return rejectWithValue(error.message);
    }
  },
);
